/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState } from "react";
import styled from "styled-components";
import { theme, Spacer, Text, FlexContainer } from "@nordcloud/gnui";
import { OrgUnitUserErrorCode } from "~/generated/graphql";
import { InlineContactsUpdate, InlineUpdate } from "~/components/Inputs";
import { useMutationHandler } from "~/hooks";
import { UpdateOrgUnit, useUpdateOrgUnit } from "~/hooks/graphql";
import { isNil, isNotNil } from "~/tools";
import { InlineTypeUpdate } from "../Edit";

type Props = {
  id: string;
  name: string;
  type: {
    id: string;
    name: string;
  };
  contact: {
    id: string;
    name: string;
  };
  description: string;
  readonlyContacts?: boolean;
};

type UpdateAction = "contact" | "description" | "name" | "type";

export function OrganizationUnitEditableDetails({
  id,
  name,
  type,
  contact,
  description,
  readonlyContacts = false,
}: Props) {
  const [updateOrgUnit, { loading }] = useUpdateOrgUnit();
  const [updateAction, setUpdateAction] = useState<UpdateAction | null>(null);

  const { runMutation } = useMutationHandler();

  const handleUpdate = async (
    loaderName: UpdateAction,
    input: UpdateOrgUnit
  ) => {
    setUpdateAction(loaderName);

    await runMutation({
      identifier: `Organization Unit ${loaderName}`,
      mutation: () =>
        updateOrgUnit({
          nid: id,
          input,
        }),
      successAction: "updated",
      userErrorParser: (result) => {
        const userErrors = result?.data?.updateOrgUnitV4?.userErrors;
        if (isNil(userErrors)) {
          return undefined;
        }

        return userErrors.code === OrgUnitUserErrorCode.DuplicateOrgUnitName &&
          isNotNil(input.name)
          ? `Organizational Unit with name "${input.name}" already exists`
          : undefined;
      },
    });

    setUpdateAction(null);
  };

  const activeLoader = loading ? updateAction : null;

  return (
    <div css={{ paddingBottom: theme.spacing.spacing02 }}>
      <Text weight="medium">Organizational Unit Details</Text>
      <FlexContainer alignItems="center">
        <DetailsTitle>Unit Name:</DetailsTitle>
        <InlineUpdate
          value={name}
          isLoading={activeLoader === "name"}
          onSubmit={(formData) =>
            handleUpdate("name", {
              name: formData.value,
            })
          }
        />
      </FlexContainer>
      <Spacer height={theme.spacing.spacing02} />
      <FlexContainer alignItems="center">
        <DetailsTitle>Unit Type:</DetailsTitle>
        <InlineTypeUpdate
          id={type.id}
          name={type.name}
          isLoading={activeLoader === "type"}
          onChange={(newTypeId) => handleUpdate("type", { typeId: newTypeId })}
        />
      </FlexContainer>
      <Spacer height={theme.spacing.spacing02} />
      <FlexContainer alignItems="center">
        <DetailsTitle>Contact Person:</DetailsTitle>
        <InlineContactsUpdate
          person={contact}
          isLoading={activeLoader === "contact"}
          readonly={readonlyContacts}
          onSubmit={(formData) =>
            handleUpdate("contact", {
              contactId: formData.personId,
            })
          }
        />
      </FlexContainer>
      <Spacer height={theme.spacing.spacing02} />
      <FlexContainer alignItems="center">
        <DetailsTitle>Description:</DetailsTitle>
        <InlineUpdate
          value={description}
          isLoading={activeLoader === "description"}
          onSubmit={(formData) =>
            handleUpdate("description", {
              description: formData.value,
            })
          }
        />
      </FlexContainer>
    </div>
  );
}

const DetailsTitle = styled.div`
  float: left;
  width: 11rem;
  word-break: break-all;
  color: ${theme.color.text.text02};
`;
